import { observeWindowSize } from "@utils/observeSize"
import { useHydrating } from "eddev/routing"
import {
  AnimatePresence,
  motion,
  SpringOptions,
  springValue,
  transformValue,
  useMotionValue,
  useMotionValueEvent,
} from "motion/react"
import { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

type Props = {
  enabled?: boolean
  render(side: "left" | "right"): ReactNode
}

/**
 * Helper function which continuously monitors mouse position, and renders content at the mouse position when enabled
 */
export function MouseFollower(props: Props) {
  const hydrating = useHydrating()
  const mouse = useMouse()
  const [side, setSide] = useState<"left" | "right">("left")
  const windowSize = useRef(0)

  useMotionValueEvent(mouse.x, "change", (val) => {
    setSide(val > windowSize.current - 200 ? "right" : "left")
  })

  useEffect(() => {
    observeWindowSize(({ width }) => {
      windowSize.current = width
    })
  })

  if (hydrating) return null

  return createPortal(
    <AnimatePresence>
      {props.enabled && (
        <motion.div
          initial={{ opacity: 0, filter: "blur(10px)" }}
          animate={{ opacity: 1, filter: "none" }}
          exit={{ opacity: 0, filter: "blur(10px)" }}
          className="fixed top-0 left-0 z-50 pointer-events-none"
          style={{
            x: mouse.x,
            y: mouse.y,
          }}
        >
          {props.render(side)}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

function useMouse() {
  const pos = useMotionValue({ x: 0, y: 0 })
  const spring = useMemo(() => {
    const conf: SpringOptions = {
      stiffness: 450,
      damping: 50,
    }
    return {
      x: springValue(
        transformValue(() => pos.get().x),
        conf,
      ),
      y: springValue(
        transformValue(() => pos.get().y),
        conf,
      ),
    }
  }, [])

  useEffect(() => {
    const mouseMove = (e: MouseEvent) => {
      pos.set({
        x: e.clientX,
        y: e.clientY,
      })
    }
    window.addEventListener("mousemove", mouseMove)
    return () => {
      window.removeEventListener("mousemove", mouseMove)
    }
  }, [])

  return spring
}
